import { Button, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Dropmenu, Search } from '../../Components';
import {
   useGetUsersQuery,
   useUpdateStatusMutation,
   useDeleteUserMutation,
} from '../../Redux/Service/UserApi';
import { Link, useNavigate } from 'react-router-dom';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { toast } from 'react-toastify';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import StyledDashboardLayout from '../style';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useModuleHasPermissions } from '../../hooks/useModulePermissions';

function UserList() {
   const navigate = useNavigate();
   const [userData, setUserData] = useState(null);
   const [searchQuery, setSearchQuery] = useState('');
   const [page, setPage] = useState(1); // DataTable is 1-based
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const [refresh, setRefresh] = useState(false);
   const MySwal = withReactContent(Swal);
   const hasPermissions = useModuleHasPermissions('user');
   const {
      data: users,
      refetch,
      isLoading,
      isError,
      error,
   } = useGetUsersQuery({
      limit: rowsPerPage,
      page: page - 1, // API is 0-based
      orderBy: 'DESC',
      search: searchQuery,
   });
   const [updateStatus] = useUpdateStatusMutation();
   const [deleteUser] = useDeleteUserMutation();

   const customStyles = {
      rows: {
         style: {
            margin: '0px',
            padding: 0,
            borderRadius: '10px',
         },
      },
      headCells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontWeight: 600,
            fontSize: 16,
            background: '#3F30FF1A',
         },
      },
      cells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontSize: 15,
            fontWeight: 400,
            color: '#726E6E',
         },
      },
   };

   const paginationComponentOptions = {
      rowsPerPageText: 'Filas por página',
      rangeSeparatorText: 'de',
      selectAllRowsItem: true,
      selectAllRowsItemText: 'Todos',
   };

   useEffect(() => {
      refetch();
   }, [refetch, searchQuery, page, rowsPerPage, refresh]);

   useEffect(() => {
      if (!isLoading && !isError) {
         setUserData(users?.response?.data);
      }
   }, [isLoading, isError, users]);

   const handleEdit = (id) => {
      navigate(`/user-edit/${id}`);
   };

   const handleView = (id) => {
      navigate(`/user/${id}`);
   };

   const handleSearch = (search) => {
      setSearchQuery(search);
   };

   const handleStatus = async (id, role) => {
      try {
         const userDetails = JSON.parse(localStorage.getItem('state') || '{}')?.user;
         if (userDetails?.id === id) return toast.error('You cannot change your own status!');
         const updateStatusData = await updateStatus(id);
         console.log(updateStatusData, 'oioioiooio');
         if (updateStatusData) {
            const updatedUserData = [...userData];
            const index = updatedUserData.findIndex((user) => user.id === id);

            if (index !== -1) {
               updatedUserData[index] = {
                  ...updatedUserData[index],
                  status: !updatedUserData[index].status,
               };
               toast.success(updateStatusData.data?.message);
               setUserData(updatedUserData);
            } else {
               toast.error('User Not Found!');
            }
         } else {
            toast.error('Failed to update status!');
         }
      } catch (error) {
         toast.error('An error occurred while updating status!');
      }
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: 'Are you sure you want to delete this account?',
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      try {
         const res = await deleteUser(id);
         if (res?.error) {
            await MySwal.fire('Denied!', res.error?.data?.message || 'Delete failed', 'error');
         } else {
            await MySwal.fire('Deleted!', 'Account deleted successfully', 'success');
            setRefresh((prev) => !prev);
         }
      } catch (err) {
         await MySwal.fire('Error', err?.message || 'Delete failed', 'error');
      }
   };

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => index + 1,
         button: false,
      },
      {
         name: 'Name',
         selector: (row) => row.name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.name}</div>
         ),
      },
      {
         name: 'Role',
         selector: (row) => row.role,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.role}</div>
         ),
      },
      {
         name: 'Department',
         selector: (row) => row.department,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.department}</div>
         ),
      },
      {
         name: 'Email',
         selector: (row) => row.email,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.email}</div>
         ),
      },
      {
         name: 'Phone No',
         selector: (row) => row.phoneNo,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.phoneNo}</div>
         ),
      },
      {
         name: 'Status',
         selector: (row) =>
            row.status ? (
               <div>
                  <Button
                     size="medium"
                     className="toggleBtn"
                     disabled={!hasPermissions?.canUpdate}
                     onClick={() => {
                        handleStatus(row.id, row.role);
                     }}
                  >
                     <ToggleOnIcon
                        sx={{
                           color: 'success.main',
                           ...(!hasPermissions?.canUpdate && { opacity: 0.5 }),
                        }}
                     />{' '}
                     {row.status}{' '}
                  </Button>
               </div>
            ) : (
               <div>
                  <Button
                     size="medium"
                     className="toggleBtn"
                     disabled={!hasPermissions?.canUpdate}
                     onClick={() => {
                        handleStatus(row.id);
                     }}
                  >
                     <ToggleOffIcon sx={{ fill: 'red' }} /> {row.status}{' '}
                  </Button>
               </div>
            ),
      },
      {
         name: 'Action',
         cell: (row) => {
            if (row.isSuperAdmin) return;
            return (
               <div>
                  <Dropmenu
                     handleEdit={() => handleEdit(row.id)}
                     handleView={() => handleView(row.id)}
                     handleDelete={() => handleDelete(row.id)}
                     canEdit={hasPermissions?.canUpdate}
                     canDelete={hasPermissions?.canDelete}
                  />
               </div>
            );
         },
         button: false,
      },
   ];

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Users</h1>
                     </div>
                     {hasPermissions?.canAdd && (
                        <div className="btnWrap">
                           <Link to="/user-add" className="btn addBtn">
                              <PersonAddAlt1Icon /> Add User
                           </Link>
                        </div>
                     )}
                  </div>
               </div>
            </div>
            <div className="card m-3 customCard">
               {isLoading ? (
                  <div>Loading...</div>
               ) : isError ? (
                  <div>Error: {error?.message}</div>
               ) : (
                  <div>
                     <Grid container spacing={2}>
                        <Grid item md={6} xs={12}></Grid>
                        <Grid item md={6} xs={12} display="flex" justifyContent="flex-end">
                           <Search onSearch={(query) => handleSearch(query)} />
                        </Grid>
                     </Grid>

                     <DataTable
                        columns={columns}
                        data={userData?.map(
                           ({ id, name, email, phoneNo, role, department, status }) => ({
                              id,
                              name,
                              email,
                              phoneNo,
                              role: role && role?.name,
                              isSuperAdmin: role && role?.isSuperAdmin,
                              department: department?.name,
                              status,
                           }),
                        )}
                        pagination
                        paginationServer
                        paginationTotalRows={users?.response?.pagination?.total || 0}
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[5, 10, 20, 50, 100]}
                        paginationDefaultPage={page}
                        onChangePage={setPage}
                        onChangeRowsPerPage={(newPerPage, newPage) => {
                           setRowsPerPage(newPerPage);
                           setPage(1); // Reset to first page when page size changes
                        }}
                        paginationComponentOptions={paginationComponentOptions}
                        customStyles={customStyles}
                        className="dataTable"
                     />
                  </div>
               )}
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default UserList;
